import { useFormik } from 'formik';
import { dateTimeFormat, getDefaultVesselValue } from 'GenericForms/Helper';
import Layout from 'HorizontalMenu/Menu';
import { RootState } from 'index';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { Col, Container, Label, Navbar, Row, Input, Button, Card } from 'reactstrap';
import { customStyle } from 'shared/CommonCSS';
import { OnboardingStatus } from 'shared/constants';
import {
    getVesselsAction,
    setOnboardedVessels,
    setVesselID,
} from 'Store/Generic/ReportingSlice';
import { userVessels } from 'utils/auth/authUtils';

interface EventBase {
    id: string;
    start: Date;
    end: Date;
}

interface FCEvent {
    id: string;
    operation_name: string;
    date: Date;
}

interface EventWithCategory extends EventBase {
    category: 'FCVoyage' | 'FC' | 'TFTanker';
    event_name?: string;
    operation_name?: string;
}

const FinalAdjustment: React.FC = () => {
    const dispatch = useDispatch();
    const { Vessels, VesselID, onboardedVessels } = useSelector(
        (state: RootState) => state.Reporting
    );
    const vessel = Vessels?.find((rec: any) => rec.id === VesselID);
    let applicableVessels = userVessels();

    // ---------- Dummy data ----------
    const eventData: any = {
        FCVoyage: [
            { id: 'fcv-1', event_name: 'Adverse Weather', start: new Date('2025-01-05T12:00'), end: new Date('2025-01-07T18:00') },
            { id: 'fcv-2', event_name: 'Equipment Failure', start: new Date('2025-01-08T06:00'), end: new Date('2025-01-10T14:00') },
            { id: 'fcv-3', event_name: 'Search and Rescue', start: new Date('2025-01-11T04:00'), end: new Date('2025-01-12T22:00') },
            { id: 'fcv-4', event_name: 'Heavy Current Zone', start: new Date('2025-01-14T09:00'), end: new Date('2025-01-15T17:00') },
            { id: 'fcv-5', event_name: 'Rough Sea Trial', start: new Date('2025-01-17T06:00'), end: new Date('2025-01-19T10:00') },
        ],
        FC: [
            { id: 'fc-0', operation_name: 'Electrical Operation', date: new Date('2025-01-01') },
            { id: 'fc-1', operation_name: 'Electrical Operation', date: new Date('2025-01-06') },
            { id: 'fc-2', operation_name: 'Boiler Operation', date: new Date('2025-01-09') },
            { id: 'fc-3', operation_name: 'Auxiliary Engine Test', date: new Date('2025-01-07') },
            { id: 'fc-4', operation_name: 'Cooling System Flush', date: new Date('2025-01-12') },
            { id: 'fc-5', operation_name: 'Emergency Drill', date: new Date('2025-01-18') },
        ],
        TFTanker: [
            { id: 'tft-1', operation_name: 'STS Loading', start: new Date('2025-01-05T14:00'), end: new Date('2025-01-07T16:00') },
            { id: 'tft-2', operation_name: 'STS Discharging', start: new Date('2025-01-08T08:00'), end: new Date('2025-01-10T12:00') },
            { id: 'tft-3', operation_name: 'Ballast Water Exchange', start: new Date('2025-01-11T10:00'), end: new Date('2025-01-13T18:00') },
            { id: 'tft-4', operation_name: 'Tank Cleaning', start: new Date('2025-01-15T07:00'), end: new Date('2025-01-16T19:00') },
            { id: 'tft-5', operation_name: 'Cargo Heating', start: new Date('2025-01-18T09:00'), end: new Date('2025-01-19T21:00') },
        ],
    };

    // ---------- Derived data ----------
    const allEvents = useMemo<EventWithCategory[]>(
        () => [
            ...eventData.FCVoyage.map((e: any) => ({ ...e, category: 'FCVoyage' as const })),
            ...eventData.FC.map((e: any) => ({
                ...e,
                start: new Date(new Date(e.date).setHours(0, 0, 0, 0)),
                end: new Date(new Date(e.date).setHours(23, 59, 59, 999)),
                category: 'FC' as const,
            })),
            ...eventData.TFTanker.map((e: any) => ({ ...e, category: 'TFTanker' as const })),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const eventsOverlap = (a: EventBase, b: EventBase) => a.start < b.end && b.start < a.end;

    const getConflicts = (id: string) => {
        const ev = allEvents.find(e => e.id === id);
        if (!ev) return [];
        return allEvents.filter(e => e.id !== id && eventsOverlap(ev, e)).map(e => e.id);
    };

    const allSelectedIds = (values: FormValues): string[] => [
        ...values.FCVoyage,
        ...values.FC,
        ...values.TFTanker,
    ];

    const canSelect = (id: string, values: FormValues) => {
        const conflicts = getConflicts(id);
        const selectedIds = allSelectedIds(values);
        return !conflicts.some(conf => selectedIds.includes(conf));
    };

    const eventDates = useMemo(() => {
        const dateStrings = new Set<string>();
        allEvents.forEach(event => {
            let current = new Date(event.start);
            const end = new Date(event.end);
            while (current <= end) {
                dateStrings.add(current.toDateString());
                current.setDate(current.getDate() + 1);
            }
        });
        return Array.from(dateStrings)
            .map(d => new Date(d))
            .sort((a, b) => a.getTime() - b.getTime());
    }, [allEvents]);

    const getEventsForDate = (date: Date, category: 'FCVoyage' | 'FC' | 'TFTanker') => {
        if (category === 'FC')
            return eventData.FC.filter(
                (e: FCEvent) => new Date(e.date).toDateString() === date.toDateString()
            );
        return eventData[category].filter(
            (e: any) =>
                e.start <= new Date(date.setHours(23, 59, 59, 999)) &&
                e.end >= new Date(date.setHours(0, 0, 0, 0))
        );
    };

    // ---------- Formik setup ----------
    interface FormValues {
        FCVoyage: string[];
        FC: string[];
        TFTanker: string[];
    }

    const formik = useFormik<FormValues>({
        initialValues: { FCVoyage: [], FC: [], TFTanker: [] },
        onSubmit: (values) => {
            console.warn('Formik Values:', values);
        },
    });

    const handleCategoryChange = (category: keyof FormValues, id: string) => {
        const categoryValues = formik.values[category];
        if (categoryValues.includes(id)) {
            formik.setFieldValue(category, categoryValues.filter(v => v !== id));
        } else if (canSelect(id, formik.values)) {
            formik.setFieldValue(category, [...categoryValues, id]);
        }
    };

    // ---------- Redux setup ----------
    useEffect(() => {
        dispatch(getVesselsAction('vessel_master' as string));
    }, [dispatch]);

    useEffect(() => {
        if (Array.isArray(Vessels)) {
            const onboarded = Vessels.filter(
                vessel => vessel.onboarding_status === OnboardingStatus.ONBOARDED
            );
            dispatch(setOnboardedVessels(onboarded));
        }
    }, [Vessels, dispatch]);

    const handleVesselSelectionChanges = (e: any) => {
        dispatch(setVesselID(e ? e.id : null));
    };

    // ---------- Render ----------
    return (
        <React.Fragment>
            <Layout>
                <Navbar />
            </Layout>
            <div className="page-content">
                <Container fluid>
                    <form onSubmit={formik.handleSubmit}>
                        <Row className="mb-2">
                            <Col sm={4}>
                                <Label className="mb-0">Select vessel</Label>
                                <Select
                                    options={
                                        onboardedVessels.filter((item: any) => item.display === true &&
                                            applicableVessels?.includes(item?.id)) as any[]
                                    }
                                    getOptionLabel={
                                        (option: any) => option.is_under_maintenance ?
                                            `${option?.vessel_name_type_name} (Under Maintenance)` : option?.vessel_name_type_name
                                    }
                                    getOptionValue={(o: any) => o.id}
                                    isClearable
                                    onChange={handleVesselSelectionChanges}
                                    menuPortalTarget={document.body}
                                    styles={customStyle}
                                    defaultValue={getDefaultVesselValue(
                                        VesselID,
                                        vessel,
                                        onboardedVessels,
                                        applicableVessels
                                    )}
                                    isOptionDisabled={(option: any) => option.is_under_maintenance}
                                />
                            </Col>
                            <Col sm={8} className="pt-3 text-end">
                                <h3 className="m-0">CII Adjustment</h3>
                            </Col>
                        </Row>

                        {/* ===================== TABLE ===================== */}
                        <div className="table-container">
                            <table className="table">
                                <thead className="table-light sticky-header">
                                    <tr>
                                        <th>Date</th>
                                        <th>FCVoyage Events</th>
                                        <th>FC Operations</th>
                                        <th>TFTanker Operations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {eventDates.map((date, idx) => {
                                        const fcv = getEventsForDate(date, 'FCVoyage');
                                        const fc = getEventsForDate(date, 'FC');
                                        const tft = getEventsForDate(date, 'TFTanker');
                                        const maxRows = Math.max(fcv.length || 1, fc.length || 1, tft.length || 1);

                                        return Array.from({ length: maxRows }).map((_, rowIdx) => {
                                            const fcvEvent = fcv[rowIdx];
                                            const fcEvent = fc[rowIdx];
                                            const tftEvent = tft[rowIdx];

                                            return (
                                                <tr key={`${idx}-${rowIdx}`}>
                                                    {rowIdx === 0 && (
                                                        <td rowSpan={maxRows} className="fw-bold bg-light">
                                                            {dateTimeFormat(date, false, false, true)}
                                                        </td>
                                                    )}

                                                    {/* FCVoyage */}
                                                    <td className={!fcvEvent ? 'bg-light' : ''}>
                                                        {fcvEvent && (
                                                            <div className="form-check d-inline-flex align-items-center">
                                                                <Input
                                                                    type="checkbox"
                                                                    id={fcvEvent.id}
                                                                    checked={formik.values.FCVoyage.includes(fcvEvent.id)}
                                                                    disabled={
                                                                        !canSelect(fcvEvent.id, formik.values) &&
                                                                        !formik.values.FCVoyage.includes(fcvEvent.id)
                                                                    }
                                                                    onChange={() =>
                                                                        handleCategoryChange('FCVoyage', fcvEvent.id)
                                                                    }
                                                                />
                                                                <Label
                                                                    className="form-check-label ms-2 mb-0"
                                                                    htmlFor={fcvEvent.id}
                                                                >
                                                                    {fcvEvent.event_name}
                                                                </Label>
                                                            </div>
                                                        )}
                                                    </td>

                                                    {/* FC */}
                                                    <td className={!fcEvent ? 'bg-light' : ''}>
                                                        {fcEvent && (
                                                            <div className="form-check d-inline-flex align-items-center">
                                                                <Input
                                                                    type="checkbox"
                                                                    id={fcEvent.id}
                                                                    checked={formik.values.FC.includes(fcEvent.id)}
                                                                    disabled={
                                                                        !canSelect(fcEvent.id, formik.values) &&
                                                                        !formik.values.FC.includes(fcEvent.id)
                                                                    }
                                                                    onChange={() =>
                                                                        handleCategoryChange('FC', fcEvent.id)
                                                                    }
                                                                />
                                                                <Label
                                                                    className="form-check-label ms-2 mb-0"
                                                                    htmlFor={fcEvent.id}
                                                                >
                                                                    {fcEvent.operation_name}
                                                                </Label>
                                                            </div>
                                                        )}
                                                    </td>

                                                    {/* TFTanker */}
                                                    <td className={!tftEvent ? 'bg-light' : ''}>
                                                        {tftEvent && (
                                                            <div className="form-check d-inline-flex align-items-center">
                                                                <Input
                                                                    type="checkbox"
                                                                    id={tftEvent.id}
                                                                    checked={formik.values.TFTanker.includes(tftEvent.id)}
                                                                    disabled={
                                                                        !canSelect(tftEvent.id, formik.values) &&
                                                                        !formik.values.TFTanker.includes(tftEvent.id)
                                                                    }
                                                                    onChange={() =>
                                                                        handleCategoryChange('TFTanker', tftEvent.id)
                                                                    }
                                                                />
                                                                <Label
                                                                    className="form-check-label ms-2 mb-0"
                                                                    htmlFor={tftEvent.id}
                                                                >
                                                                    {tftEvent.operation_name}
                                                                </Label>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* ===================== CII SUMMARY CARD ===================== */}
                        <Row className="mb-2 justify-content-end mt-4">
                            <Col lg={3}>
                                <Card className="card-custom p-3 ms-auto">
                                    <Row className="align-items-start">
                                        <Col lg={8}>
                                            <h6 className="mb-3">CII Calculation</h6>
                                            <span>CII Ratio - <strong>2.45</strong></span>
                                        </Col>
                                        <Col lg={4} className="text-center">
                                            <div className="CII-rating-B d-flex align-items-center justify-content-center"
                                                style={{ height: '60px', borderRadius: '8px' }}>
                                                <Label className="mb-0 fs-4 fw-bold text-light" id="rating">B</Label>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="mt-4">
                                        <Col className="d-flex justify-content-end gap-2">
                                            <Button type="submit" color="primary">Save</Button>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </form>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default FinalAdjustment;